import { useContext, useEffect, useRef, useState } from "react";
import { PlayerAction } from "../../../types";
import StateButton from "../../common/state-button/state-button";
import { useAppSelector } from "../../../redux/store";
import { now } from "../../../time";

import { GameContext } from "../../../contexts";
import Button from "@/components/ui/button";
import useInterval from "../../common/use-interval/use-interval";
import {
  selectGameSettings,
  selectIsHost,
  selectLoading,
  selectPlayerCurrentSubmission,
} from "../../../redux/selectors";

type Props = {
  onPlayerAction: (playerAction: PlayerAction) => void;
  deadline: number;

  done?: boolean;
  onDone: () => void;
};

const StoryFooter = ({ done, onDone, deadline }: Props) => {
  const { submit } = useContext(GameContext);

  const promptIndex = useRef(0);
  const bootSent = useRef(false);

  // Use narrow selectors to avoid rerenders on unrelated state changes
  const gameSettings = useAppSelector(selectGameSettings);
  const isHost = useAppSelector(selectIsHost);
  const loading = useAppSelector(selectLoading);
  const currentSubmission = useAppSelector(selectPlayerCurrentSubmission);

  const [promptValue, setPromptValue] = useState("need a prompt?");
  const [timeRemaining, setTimeRemaining] = useState<number>(deadline - now());

  const paddedMinutes = Math.floor(timeRemaining / (1000 * 60))
    .toString()
    .padStart(2, "0");
  const paddedSeconds = (Math.floor(timeRemaining / 1000) % 60)
    .toString()
    .padStart(2, "0");

  useEffect(() => {
    if (
      (gameSettings.solo_writing ||
        gameSettings.forced_prompts) &&
      currentSubmission?.meta.prompt
    ) {
      setPromptValue(currentSubmission.meta.prompt);
    }
  }, [
    gameSettings.forced_prompts,
    gameSettings.solo_writing,
    currentSubmission?.meta.prompt,
  ]);

  // Immediately sync when deadline changes (prevents garbage display during transitions)
  useEffect(() => {
    setTimeRemaining(deadline - now());
  }, [deadline]);

  useInterval(() => {
    setTimeRemaining(deadline - now());
  }, 1000);

  function handleBoot() {
    let playerAction = {
      body: {
        vip_action: {
          skip_submission: true,
        },
      },
      code: "game/vip",
    };
    submit(playerAction);
    bootSent.current = true;
  }

  function handleDone() {
    onDone();
  }

  useEffect(() => {
    if (!loading && bootSent.current) {
      bootSent.current = false;
    }
  }, [loading]);

  function handleVip() {
    handleBoot();
  }

  // Mature and All Ages prompts are given 50/50 odds
  function handlePrompt() {
    if (currentSubmission?.prompts) {
      let promptLength = currentSubmission.prompts.length;
      setPromptValue(
        currentSubmission.prompts[
        promptIndex.current++ % promptLength
        ]
      );
    }
  }

  function disableSkip() {
    return (loading && bootSent.current) || timeRemaining <= 0;
  }

  return (
    <div className="self-stretch mt-auto flex flex-row justify-start items-center h-16 space-x-2 text-base p-0.5 bg-(--color-gray-med) border-[thick] border-solid border-(--color-gray-drker) border-r-(--color-gray-light) border-b-(--color-gray-light) border-t-gray-shadow">
      {!gameSettings.solo_writing ? (
        <Button
          className={"self-stretch"}
          onClick={handlePrompt}
          disabled={timeRemaining <= 0 || gameSettings.forced_prompts}
        >
          {promptValue}
        </Button>
      ) : (
        ""
      )}

      <div className="flex-1 self-stretch flex flex-row justify-end space-x-2">
        {isHost &&
          !gameSettings.solo_writing ? (
          <div className="self-stretch flex justify-center items-stretch text-sm w-24">
            <StateButton
              disabled={disableSkip()}
              onClick={handleVip}
              states={["force skip all", "are you sure?", "SKIP!"]}
            />
          </div>
        ) : (
          ""
        )}

        <div className="self-stretch flex justify-center items-center min-w-0">
          <Button
            onClick={handleDone}
            disabled={timeRemaining <= 0 || loading}
            className={"self-stretch"}
          >
            {done ? "done! 🚩" : "i'm done!"}
          </Button>
        </div>
        <div className="self-stretch flex flex-col justify-center items-center text-xl">
          <label className={"text-base"}>
            {timeRemaining <= 0 ? "Time's up!" : "Timer"}
          </label>
          {/*<label className={"text-sm"}>{'Time\'s up!'}</label>*/}
          {timeRemaining <= 0 ? (
            <strong>Saving...</strong>
          ) : timeRemaining < 10000 ? (
            <div
              className={"story-time-footer-warning"}
            >{`${paddedMinutes}:${paddedSeconds}`}</div>
          ) : (
            <div>{`${paddedMinutes}:${paddedSeconds}`}</div>
          )}
        </div>
      </div>
    </div>
  );
};

export default StoryFooter;
